import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "../lib/apiClient";
import type {
  CardInvalidationListResponse,
  CardInvalidationPendingCountResponse,
  MicrogroupApprovalDecisionPayload,
  MicrogroupApprovalDecisionResponse,
  MicrogroupDissolveResponse,
  MicrogroupInvitationListResponse,
  MicrogroupInviteResponse,
  MicrogroupOverviewResponse,
} from "../schemas/microgroup";
import type { MicrogroupOnboardingTokenInfo } from "../schemas/microgroupOnboarding";
import {
  toMicrogroupDissolveApiPayload,
  type MicrogroupDissolveInput,
} from "../schemas/microgroupDissolve";
import {
  toMicrogroupInviteApiPayload,
  type MicrogroupInviteInput,
} from "../schemas/microgroupInvite";

/**
 * UC-006 Mikroskupina queries + mutations (Phase 28).
 *
 * Sub-admin (Milan) routes:
 *   - useMicrogroupOverview() — GET /api/tenant/microgroups/me
 *   - useMicrogroupInvite() — POST /api/tenant/microgroups/{id}/invite
 *   - useMicrogroupDissolve() — POST /api/tenant/microgroups/{id}/dissolve
 *
 * Admin (Petr) routes:
 *   - useAdminMicrogroupInvitations(status) — GET queue
 *   - useApproveInvitation() / useRejectInvitation() — decision mutations
 *
 * Cards-to-invalidate queue:
 *   - useCardsToInvalidate()
 *   - useCardsToInvalidatePendingCount() — Application Shell critical Banner
 *
 * Onboarding landing:
 *   - useMicrogroupOnboardingToken(token) — GET token info (idempotent)
 *   - useMicrogroupCompleteOnboarding() — POST complete (consume + JWT issue)
 */

const KEY_OVERVIEW = ["tenant", "microgroups", "me"] as const;
const KEY_ADMIN_INVITATIONS = (status: string) =>
  ["admin", "microgroup-invitations", status] as const;
const KEY_CARDS = ["admin", "cards-to-invalidate"] as const;
const KEY_CARDS_PENDING = ["admin", "cards-to-invalidate", "pending-count"] as const;
const KEY_ONBOARDING_TOKEN = (token: string) =>
  ["public", "microgroup", "onboarding", token] as const;

export function useMicrogroupOverview() {
  return useQuery({
    queryKey: KEY_OVERVIEW,
    // "Žádná mikroskupina" = backend vrací 204 No Content → apiClient vrátí
    // undefined. react-query v5 ale nedovolí `undefined` jako data → mapujeme
    // na null. Komponenta renderuje empty state na `!data`.
    queryFn: async (): Promise<MicrogroupOverviewResponse | null> => {
      const r = await apiCall<MicrogroupOverviewResponse | undefined>(
        "/api/tenant/microgroups/me",
      );
      return r ?? null;
    },
  });
}

export function useMicrogroupInvite(microgroupId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MicrogroupInviteInput) =>
      apiCall<MicrogroupInviteResponse>(
        `/api/tenant/microgroups/${microgroupId}/invite`,
        {
          method: "POST",
          body: JSON.stringify(toMicrogroupInviteApiPayload(input)),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_OVERVIEW });
    },
  });
}

export function useMicrogroupDissolve(microgroupId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MicrogroupDissolveInput) =>
      apiCall<MicrogroupDissolveResponse>(
        `/api/tenant/microgroups/${microgroupId}/dissolve`,
        {
          method: "POST",
          body: JSON.stringify(toMicrogroupDissolveApiPayload(input)),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_OVERVIEW });
    },
  });
}

export function useAdminMicrogroupInvitations(status: string = "pending_admin_approval") {
  return useQuery({
    queryKey: KEY_ADMIN_INVITATIONS(status),
    queryFn: () =>
      apiCall<MicrogroupInvitationListResponse>(
        `/api/admin/microgroup-invitations?status=${encodeURIComponent(status)}`,
      ),
  });
}

export function useApproveInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      invitationId,
      payload,
    }: {
      invitationId: string;
      payload: MicrogroupApprovalDecisionPayload;
    }) =>
      apiCall<MicrogroupApprovalDecisionResponse>(
        `/api/admin/microgroup-invitations/${invitationId}/approve`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "microgroup-invitations"] });
    },
  });
}

export function useRejectInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      invitationId,
      payload,
    }: {
      invitationId: string;
      payload: MicrogroupApprovalDecisionPayload;
    }) =>
      apiCall<MicrogroupApprovalDecisionResponse>(
        `/api/admin/microgroup-invitations/${invitationId}/reject`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "microgroup-invitations"] });
    },
  });
}

export function useCardsToInvalidate() {
  return useQuery({
    queryKey: KEY_CARDS,
    queryFn: () =>
      apiCall<CardInvalidationListResponse>("/api/admin/cards-to-invalidate"),
  });
}

export function useCardsToInvalidatePendingCount(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEY_CARDS_PENDING,
    queryFn: () =>
      apiCall<CardInvalidationPendingCountResponse>(
        "/api/admin/cards-to-invalidate/pending-count",
      ),
    enabled: options?.enabled ?? true,
    refetchInterval: 5 * 60_000, // 5 min — Application Shell banner refresh
  });
}

export function useMicrogroupOnboardingToken(token: string | undefined) {
  return useQuery({
    queryKey: KEY_ONBOARDING_TOKEN(token ?? "—"),
    queryFn: () =>
      apiCall<MicrogroupOnboardingTokenInfo>(
        `/api/public/microgroup/consume-token/${encodeURIComponent(token ?? "")}`,
      ),
    enabled: typeof token === "string" && token.length > 0,
    // Token validation by default doesn't auto-retry — backend already vrátí
    // 410 EXPIRED_TOKEN deterministically (no transient state).
    retry: false,
  });
}

export interface MicrogroupCompleteOnboardingInput {
  token: string;
  password: string;
  consent_text_version: string;
  consent_types: string[];
}

export function useMicrogroupCompleteOnboarding() {
  return useMutation({
    mutationFn: (input: MicrogroupCompleteOnboardingInput) =>
      apiCall<{ user_id: string; tenant_id: string }>(
        `/api/public/microgroup/onboarding/${encodeURIComponent(input.token)}/complete`,
        {
          method: "POST",
          body: JSON.stringify({
            password: input.password,
            consent_text_version: input.consent_text_version,
            consent_types: input.consent_types,
          }),
        },
      ),
  });
}

export const MICROGROUP_QUERY_KEYS = {
  overview: KEY_OVERVIEW,
  adminInvitations: KEY_ADMIN_INVITATIONS,
  cards: KEY_CARDS,
  cardsPending: KEY_CARDS_PENDING,
  onboardingToken: KEY_ONBOARDING_TOKEN,
};
